import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { apiFetch } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Banner } from "@/components/ui/banner";
import {
  readEmailPrefill,
  repairUnencodedPlus,
} from "@/routes/begin-calibration/email-prefill";
import { toBeginCalibrationError } from "@/routes/begin-calibration/begin-calibration-error";

// Begin-Calibration landing (Screen 37 / UX §13.15, Story 10.10, FR38). The ONLY
// public surface of the LearnWorlds → platform handoff: the Module-12 button is a
// plain link here with `?email={{USER.EMAIL}}` as an UNTRUSTED, editable prefill.
// No session is ever established from this page — every successful path ends at
// /sign-in. The server verifies course completion itself (LearnWorlds REST API);
// identity truth is the API-returned email the response carries back.
//
// Six-state contract (one surface): ready → verifying → success-new /
// success-active / unverified, with throttled + service-error siblings. The
// unknown-learner and incomplete-course refusals share ONE generic `unverified`
// state (anti-enumeration — the copy never reveals which).
//
// skipAuthRefresh: true — genuinely pre-session (the legacy-signup precedent).

type ScreenState =
  | "ready"
  | "verifying"
  | "success-new"
  | "success-active"
  | "unverified"
  | "throttled"
  | "service-error";

// The repair runs INSIDE the schema (transform → pipe) so a hand-pasted
// space-mangled address validates and submits as its canonical `+` form.
const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Enter your course email.")
    .transform(repairUnencodedPlus)
    .pipe(z.string().email("Enter a valid email address.")),
});
type FormValues = z.infer<typeof schema>;

// Wire shape of the unwrapped `data` (apiFetch strips the { status, data }
// envelope): three outcomes, with `email` = the API-RETURNED address on the
// success outcomes and null on `unverified`.
interface VerifyResponse {
  outcome: "provisioned" | "already_active" | "unverified";
  email: string | null;
}

export function BeginCalibrationScreen() {
  const location = useLocation();
  const [state, setState] = React.useState<ScreenState>("ready");
  // The API-returned address shown in success-new ("We emailed a temporary
  // password to …") — never the submitted field value.
  const [confirmedEmail, setConfirmedEmail] = React.useState<string | null>(null);
  // Every non-`ready` state fully replaces the form, unmounting the focused CTA.
  // Focus is moved to whichever block is now mounted so keyboard/AT users land on
  // the result instead of <body> and its aria-live banner is reliably announced
  // (WCAG AA — this is a public route; mirrors the 6.8 ValidationSummaryBanner and
  // 6.7 resume-focus precedents).
  const outcomeRef = React.useRef<HTMLDivElement>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { email: readEmailPrefill(location.search) },
  });

  const verifyMutation = useMutation({
    mutationFn: (values: FormValues) =>
      apiFetch<VerifyResponse>("/auth/begin-calibration/verify", {
        method: "POST",
        // values.email is the schema-TRANSFORMED value — already repaired
        // (AC 2: normalization before submission).
        body: { email: values.email },
        skipAuthRefresh: true,
      }),
    onSuccess: (data) => {
      setConfirmedEmail(data.email);
      if (data.outcome === "provisioned") setState("success-new");
      else if (data.outcome === "already_active") setState("success-active");
      else setState("unverified");
    },
    onError: (error) => {
      const mapped = toBeginCalibrationError(error);
      if (mapped.emailMessage) {
        form.setError("email", { message: mapped.emailMessage });
        setState("ready");
        return;
      }
      setState(mapped.state ?? "service-error");
    },
  });

  React.useEffect(() => {
    if (state !== "ready") outcomeRef.current?.focus();
  }, [state]);

  const onSubmit = form.handleSubmit((values) => {
    if (verifyMutation.isPending) return;
    setState("verifying");
    verifyMutation.mutate(values);
  });

  const backToReady = () => setState("ready");

  return (
    <main className="flex min-h-screen items-center justify-center p-5">
      <div className="grid w-full max-w-[min(var(--width-sm),100%)] items-stretch gap-10 lg:max-w-[1120px] lg:grid-cols-[1.05fr_0.95fr]">
        {/* Hero panel — decorative, hidden on small viewports (Screen 37 mirrors
            the Screen 01 auth layout verbatim). */}
        <section
          className="hidden flex-col justify-center gap-5 rounded-3xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-14 lg:flex"
          aria-hidden="true"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[color:var(--bg-brand-solid)] font-semibold text-[color:var(--text-white)]">
              AM
            </div>
            <div className="flex flex-col leading-tight">
              <strong className="text-[color:var(--text-primary)]">CITS AMPS</strong>
              <span className="text-sm text-[color:var(--text-tertiary)]">
                Clinical scoring platform
              </span>
            </div>
          </div>
          <span className="text-sm font-semibold uppercase tracking-wide text-[color:var(--text-brand-secondary)]">
            Calibration onboarding
          </span>
          <p className="display-xs text-[color:var(--text-primary)]">
            You finished the AMPS course. Calibration is the next step.
          </p>
          <p className="text-md text-[color:var(--text-secondary)]">
            We confirm your course completion with LearnWorlds, prepare your platform
            account, and guide you into the five calibration case studies before live
            scoring.
          </p>
        </section>

        <Card className="w-full shadow-elevated">
          <CardHeader>
            <CardTitle>Begin calibration</CardTitle>
            <CardDescription>
              Confirm the email address you used for the AMPS course.
            </CardDescription>
          </CardHeader>

          {state === "ready" ? (
            <form className="mt-5 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
              <FormField
                label="Course email"
                error={form.formState.errors.email?.message}
                helper="Pre-filled from your course link. Edit it if it is not the address you registered with."
                required
              >
                <Input type="email" autoComplete="email" {...form.register("email")} />
              </FormField>

              {/* NOT gated on isValid: the prefill arrives untouched (RHF has not
                  validated yet) and the mockup's CTA is always enabled — an invalid
                  submit surfaces the inline field error instead. */}
              <Button type="submit" variant="primary" disabled={verifyMutation.isPending}>
                Verify my course completion
              </Button>
              <Button asChild variant="secondary">
                <Link to="/sign-in">I already have platform access — sign in</Link>
              </Button>
            </form>
          ) : null}

          {state === "verifying" ? (
            <div
              ref={outcomeRef}
              tabIndex={-1}
              className="mt-5 flex flex-col gap-3 focus:outline-none"
            >
              <Banner variant="info" role="status">
                Checking your course completion with LearnWorlds…
              </Banner>
              {/* Indeterminate progress (mockup `.progress`) — decorative. */}
              <div
                role="progressbar"
                aria-label="Verifying"
                className="h-2 w-full overflow-hidden rounded-full bg-[color:var(--bg-tertiary)]"
              >
                <span className="block h-full w-3/5 animate-pulse rounded-full bg-[color:var(--bg-brand-solid)]" />
              </div>
              <p className="text-sm text-[color:var(--text-tertiary)]">
                This usually takes a few seconds. Please keep this page open.
              </p>
            </div>
          ) : null}

          {state === "success-new" ? (
            <div
              ref={outcomeRef}
              tabIndex={-1}
              className="mt-5 flex flex-col gap-3 focus:outline-none"
            >
              <Banner variant="success" role="status">
                Course completion confirmed. Your platform account is ready.
              </Banner>
              <p className="text-sm text-[color:var(--text-secondary)]">
                We emailed a temporary password to{" "}
                <strong className="text-[color:var(--text-primary)]">
                  {confirmedEmail}
                </strong>
                . Sign in with it and set your own password — you will land on the
                calibration overview.
              </p>
              <p className="text-sm text-[color:var(--text-tertiary)]">
                The email can take a minute to arrive. Check your spam folder if you do
                not see it.
              </p>
              <Button asChild variant="primary">
                <Link to="/sign-in">Go to sign in</Link>
              </Button>
            </div>
          ) : null}

          {state === "success-active" ? (
            <div
              ref={outcomeRef}
              tabIndex={-1}
              className="mt-5 flex flex-col gap-3 focus:outline-none"
            >
              <Banner variant="success" role="status">
                Course completion confirmed. Your account is active.
              </Banner>
              <p className="text-sm text-[color:var(--text-secondary)]">
                This email address already has platform access. Sign in with your
                existing credentials to begin calibration.
              </p>
              <Button asChild variant="primary">
                <Link to="/sign-in">Go to sign in</Link>
              </Button>
            </div>
          ) : null}

          {state === "unverified" ? (
            <div
              ref={outcomeRef}
              tabIndex={-1}
              className="mt-5 flex flex-col gap-3 focus:outline-none"
            >
              <Banner variant="info" role="status">
                We could not confirm course completion for this email address.
              </Banner>
              <p className="text-sm text-[color:var(--text-secondary)]">
                Make sure you entered the address you registered with for the AMPS
                course. If you finished the course just now, completion can take a few
                minutes to register — try again shortly.
              </p>
              <p className="text-sm text-[color:var(--text-tertiary)]">
                Still stuck? Contact CITS support from your course page.
              </p>
              <Button type="button" variant="secondary" onClick={backToReady}>
                Try again
              </Button>
            </div>
          ) : null}

          {state === "throttled" ? (
            <div
              ref={outcomeRef}
              tabIndex={-1}
              className="mt-5 flex flex-col gap-3 focus:outline-none"
            >
              <Banner variant="warn" role="status">
                Too many attempts. Please wait a few minutes before trying again.
              </Banner>
              <p className="text-sm text-[color:var(--text-secondary)]">
                Verification is temporarily paused for this address. Your course
                progress is not affected.
              </p>
              <Button type="button" variant="secondary" onClick={backToReady}>
                Back
              </Button>
            </div>
          ) : null}

          {state === "service-error" ? (
            <div
              ref={outcomeRef}
              tabIndex={-1}
              className="mt-5 flex flex-col gap-3 focus:outline-none"
            >
              <Banner variant="warn" role="status">
                Verification is temporarily unavailable.
              </Banner>
              <p className="text-sm text-[color:var(--text-secondary)]">
                We could not reach the course verification service. Please try again in
                a few minutes — your course progress is not affected.
              </p>
              <Button type="button" variant="secondary" onClick={backToReady}>
                Try again
              </Button>
            </div>
          ) : null}
        </Card>
      </div>
    </main>
  );
}

export default BeginCalibrationScreen;
